import React, { useState } from 'react';
import { Box, Button, IconButton, TextField, Typography } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useInput } from 'react-admin';

interface KeyValuePair {
    key: string;
    value: string;
}

interface KeyValueInputProps {
    source: string;
    label?: string;
    fullWidth?: boolean;
}

export const KeyValueInput: React.FC<KeyValueInputProps> = ({
    source,
    label = 'Data',
    fullWidth = true,
}) => {
    const {
        field,
        fieldState: { error },
    } = useInput({ source });

    const [pairs, setPairs] = useState<KeyValuePair[]>(() => {
        if (field.value && typeof field.value === 'object') {
            return Object.entries(field.value).map(([key, value]) => ({
                key,
                value: String(value),
            }));
        }
        return [{ key: '', value: '' }];
    });

    const handleAddPair = () => {
        setPairs([...pairs, { key: '', value: '' }]);
    };

    const handleRemovePair = (index: number) => {
        const newPairs = pairs.filter((_, i) => i !== index);
        setPairs(newPairs);
        updateFieldValue(newPairs);
    };

    const handleKeyChange = (index: number, newKey: string) => {
        const newPairs = [...pairs];
        newPairs[index].key = newKey;
        setPairs(newPairs);
        updateFieldValue(newPairs);
    };

    const handleValueChange = (index: number, newValue: string) => {
        const newPairs = [...pairs];
        newPairs[index].value = newValue;
        setPairs(newPairs);
        updateFieldValue(newPairs);
    };

    const updateFieldValue = (currentPairs: KeyValuePair[]) => {
        const dataObject = currentPairs.reduce((acc, pair) => {
            if (pair.key.trim()) {
                acc[pair.key] = pair.value;
            }
            return acc;
        }, {} as Record<string, string>);

        field.onChange(dataObject);
    };

    return (
        <Box sx={{ width: fullWidth ? '100%' : 'auto', my: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {label}
            </Typography>

            {pairs.map((pair, index) => (
                <Box
                    key={index}
                    sx={{
                        display: 'flex',
                        gap: 1,
                        mb: 1,
                        alignItems: 'center',
                    }}
                >
                    <TextField
                        label="Key"
                        value={pair.key}
                        onChange={e => handleKeyChange(index, e.target.value)}
                        size="small"
                        sx={{ flex: 1 }}
                        error={!pair.key && pair.value ? true : false}
                        helperText={!pair.key && pair.value ? 'Key is required' : ''}
                    />
                    <TextField
                        label="Value"
                        value={pair.value}
                        onChange={e => handleValueChange(index, e.target.value)}
                        size="small"
                        sx={{ flex: 1 }}
                    />
                    <IconButton
                        onClick={() => handleRemovePair(index)}
                        color="error"
                        size="small"
                        disabled={pairs.length === 1}
                    >
                        <DeleteIcon />
                    </IconButton>
                </Box>
            ))}

            <Button
                startIcon={<AddIcon />}
                onClick={handleAddPair}
                variant="outlined"
                size="small"
                sx={{ mt: 1 }}
            >
                Add Field
            </Button>

            {error && (
                <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                    {error.message}
                </Typography>
            )}
        </Box>
    );
};
